import { useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';

export interface UseFormNavigationBlockOptions {
  isDirty: boolean;
  onDiscard?: () => void;
  onDirtyStateChange?: (isDirty: boolean) => void;
}

export interface UseFormNavigationBlockReturn {
  showNavigationModal: boolean;
  handleNavigationDiscard: () => void;
  handleNavigationCancel: () => void;
}

/**
 * Custom hook to block navigation when form has unsaved changes
 *
 * Handles both:
 * - React Router navigation (back button, link clicks)
 * - Browser navigation (refresh, close tab)
 *
 * @param options - Configuration options
 * @param options.isDirty - Whether the form has unsaved changes
 * @param options.onDiscard - Callback to discard changes and reset form state
 * @param options.onDirtyStateChange - Callback when dirty state changes
 * @returns Modal state and handlers
 *
 * @example
 * ```tsx
 * const { showNavigationModal, handleNavigationDiscard, handleNavigationCancel } =
 *   useFormNavigationBlock({
 *     isDirty: form.isDirty(),
 *     onDiscard: () => {
 *       form.setValues(initialFormValues);
 *       form.resetDirty();
 *     }
 *   });
 * ```
 */
export function useFormNavigationBlock({
  isDirty,
  onDiscard,
  onDirtyStateChange,
}: UseFormNavigationBlockOptions): UseFormNavigationBlockReturn {
  const [showNavigationModal, setShowNavigationModal] = useState(false);

  // Use ref to track if we're allowing navigation (to bypass blocker)
  const allowNavigationRef = useRef(false);

  // Block React Router navigation when form has unsaved changes
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    // Allow navigation if we explicitly set the flag
    if (allowNavigationRef.current) {
      return false;
    }
    return isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  // Show modal when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowNavigationModal(true);
    }
  }, [blocker.state]);

  // Notify parent of dirty state changes
  useEffect(() => {
    if (onDirtyStateChange) {
      onDirtyStateChange(isDirty);
    }
  }, [isDirty, onDirtyStateChange]);

  // Browser navigation protection (refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Handle discard changes and proceed with navigation
  const handleNavigationDiscard = () => {
    setShowNavigationModal(false);

    // Call discard callback if provided
    if (onDiscard) {
      onDiscard();
    }

    // Set flag to allow navigation and proceed
    allowNavigationRef.current = true;
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }

    // Reset the flag after a short delay to re-enable blocking for future changes
    setTimeout(() => {
      allowNavigationRef.current = false;
    }, 100);
  };

  // Handle cancel navigation and keep editing
  const handleNavigationCancel = () => {
    setShowNavigationModal(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  };

  return {
    showNavigationModal,
    handleNavigationDiscard,
    handleNavigationCancel,
  };
}
